import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  setDoc,
  where,
  type Timestamp,
} from 'firebase/firestore';
import { appConfig } from '@/config/app';
import { getFirestoreDb } from '@/services/firebase';
import {
  buildTimestampPayload,
  timestampToDate,
} from '@/modules/inventory/services/firestoreMapper';
import { COMMERCIAL_OPERATIONS_COLLECTION } from '@/modules/commercial/services/constants';
import type {
  CommercialOperation,
  CommercialOperationInput,
  CommercialOperationStatus,
} from '@/modules/commercial/types';
import { getClient } from '@/modules/commercial/services/clientsService';
import { saveReservation } from '@/modules/reservations/services';
import type { ReservationInput } from '@/modules/reservations/types';
import { publishCommercialOperationCreated } from '@/services/events/eventPublisher.service';
import { recordEntityWrite, toAuditSnapshot } from '@/services/audit';
import { withFirestoreError } from '@/shared/utils/firestoreError';

type OperationFirestoreDoc = {
  companyId: string;
  clientId: string | null;
  type: CommercialOperation['type'];
  status: CommercialOperationStatus;
  title: string;
  description: string;
  reservationId: string | null;
  logisticsOrderIds: string[];
  inventoryItemIds: string[];
  startDate: string;
  endDate: string;
  subtotal: number;
  taxAmount: number;
  total: number;
  notes: string;
  createdBy: string;
  createdAt: Timestamp;
  updatedAt: Timestamp;
};

function mapOperation(
  id: string,
  data: OperationFirestoreDoc,
): CommercialOperation {
  return {
    id,
    companyId: data.companyId,
    clientId: data.clientId,
    type: data.type,
    status: data.status,
    title: data.title,
    description: data.description ?? '',
    reservationId: data.reservationId,
    logisticsOrderIds: data.logisticsOrderIds ?? [],
    inventoryItemIds: data.inventoryItemIds ?? [],
    startDate: data.startDate ?? '',
    endDate: data.endDate ?? '',
    subtotal: data.subtotal ?? 0,
    taxAmount: data.taxAmount ?? 0,
    total: data.total ?? 0,
    notes: data.notes ?? '',
    createdBy: data.createdBy,
    createdAt: timestampToDate(data.createdAt),
    updatedAt: timestampToDate(data.updatedAt),
  };
}

function computeTotals(input: CommercialOperationInput): {
  subtotal: number;
  taxAmount: number;
  total: number;
} {
  const subtotal = Math.max(0, input.subtotal);
  const taxAmount = Math.max(0, input.taxAmount);
  const total =
    input.total > 0 ? input.total : Math.round((subtotal + taxAmount) * 100) / 100;
  return { subtotal, taxAmount, total };
}

export async function listCommercialOperations(
  companyId: string = appConfig.companyId,
): Promise<CommercialOperation[]> {
  const q = query(
    collection(getFirestoreDb(), COMMERCIAL_OPERATIONS_COLLECTION),
    where('companyId', '==', companyId),
  );
  const snap = await getDocs(q);
  return snap.docs
    .map((d) => mapOperation(d.id, d.data() as OperationFirestoreDoc))
    .sort((a, b) => b.updatedAt.getTime() - a.updatedAt.getTime());
}

export async function listCommercialOperationsForReservation(
  reservationId: string,
): Promise<CommercialOperation[]> {
  const all = await listCommercialOperations();
  return all.filter((o) => o.reservationId === reservationId);
}

export async function getCommercialOperation(
  id: string,
): Promise<CommercialOperation | null> {
  const snap = await getDoc(
    doc(getFirestoreDb(), COMMERCIAL_OPERATIONS_COLLECTION, id),
  );
  if (!snap.exists()) return null;
  return mapOperation(snap.id, snap.data() as OperationFirestoreDoc);
}

export async function saveCommercialOperation(
  input: CommercialOperationInput,
  createdBy: string,
  id?: string,
): Promise<CommercialOperation> {
  return withFirestoreError(async () => {
  const totals = computeTotals(input);
  const ref = id
    ? doc(getFirestoreDb(), COMMERCIAL_OPERATIONS_COLLECTION, id)
    : doc(collection(getFirestoreDb(), COMMERCIAL_OPERATIONS_COLLECTION));
  const existing = await getDoc(ref);
  const before = existing.exists()
    ? toAuditSnapshot(
        mapOperation(existing.id, existing.data() as OperationFirestoreDoc),
      )
    : undefined;
  const payload = {
    companyId: appConfig.companyId,
    clientId: input.clientId,
    type: input.type,
    status: input.status,
    title: input.title.trim(),
    description: input.description.trim(),
    reservationId: input.reservationId,
    logisticsOrderIds: input.logisticsOrderIds,
    inventoryItemIds: input.inventoryItemIds,
    startDate: input.startDate,
    endDate: input.endDate,
    subtotal: totals.subtotal,
    taxAmount: totals.taxAmount,
    total: totals.total,
    notes: input.notes.trim(),
    createdBy: existing.exists()
      ? (existing.data() as OperationFirestoreDoc).createdBy
      : createdBy,
    ...buildTimestampPayload(!existing.exists()),
  };
  await setDoc(ref, payload, { merge: true });
  const saved = await getDoc(ref);
  if (!saved.exists()) throw new Error('No se pudo guardar la operación.');
  const result = mapOperation(saved.id, saved.data() as OperationFirestoreDoc);
  const isCreate = !existing.exists();
  recordEntityWrite({
    entityType: 'commercialOperation',
    entityId: result.id,
    isCreate,
    ...(before !== undefined ? { before } : {}),
    after: toAuditSnapshot(result),
  });
  if (isCreate) {
    void publishCommercialOperationCreated({
      operationId: result.id,
      title: result.title,
      status: result.status,
    });
  }
  return result;
  }, 'commercialOperation');
}

/** Genera reserva draft vinculada a operación rental y actualiza la operación. */
export async function createReservationForOperation(
  operationId: string,
  createdBy: string,
): Promise<CommercialOperation> {
  const operation = await getCommercialOperation(operationId);
  if (!operation) throw new Error('Operación no encontrada.');
  if (operation.type !== 'rental') {
    throw new Error('Solo operaciones de alquiler pueden generar reserva.');
  }
  if (!operation.startDate || !operation.endDate) {
    throw new Error('Definí fechas de inicio y fin en la operación.');
  }
  if (operation.startDate >= operation.endDate) {
    throw new Error('La fecha de inicio debe ser anterior a la de fin.');
  }

  let clientName = '';
  if (operation.clientId) {
    const client = await getClient(operation.clientId);
    clientName = client?.name ?? '';
  }

  const reservationInput: ReservationInput = {
    title: operation.title,
    description: operation.description,
    inventoryItemIds: operation.inventoryItemIds,
    clientName,
    startDate: operation.startDate,
    endDate: operation.endDate,
    status: 'draft',
    notes: operation.notes,
  };

  const reservation = await saveReservation(reservationInput, createdBy);

  return saveCommercialOperation(
    {
      clientId: operation.clientId,
      type: operation.type,
      status: operation.status,
      title: operation.title,
      description: operation.description,
      reservationId: reservation.id,
      logisticsOrderIds: operation.logisticsOrderIds,
      inventoryItemIds: operation.inventoryItemIds,
      startDate: operation.startDate,
      endDate: operation.endDate,
      subtotal: operation.subtotal,
      taxAmount: operation.taxAmount,
      total: operation.total,
      notes: operation.notes,
    },
    createdBy,
    operationId,
  );
}
